import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Icon, Modal, ModalHeader, ModalBody } from '@evertrue/et-components';
import { fetchJourneysCreatedByUser } from 'apps/journeys/utils/api-requests/journey-index';
import { fetchStepsByJourneyId } from 'apps/journeys/utils/api-requests/journey-details';
import { convertJourneyStepsToV2Steps } from '../../../utils/journey-conversion';
import { getTaskTypeInfo } from '../../../utils/cadence-step-utils';
import SessionStore from 'base/session/session-store';
import { getDateString } from 'base/new-utils';

const CADENCE_1_JOURNEY_TYPES = ['QUALIFICATION', 'CULTIVATION', 'SOLICITATION', 'STEWARDSHIP', 'OTHER'];

const ImportJourneyModal = ({ isOpen, onClose, onImport }) => {
  const [journeys, setJourneys] = useState([]);
  const [loadingJourneys, setLoadingJourneys] = useState(false);
  const [selectedJourneyId, setSelectedJourneyId] = useState('');
  const [previewSteps, setPreviewSteps] = useState(null);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [error, setError] = useState(null);
  const latestRequestedId = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingJourneys(true);
    setSelectedJourneyId('');
    setPreviewSteps(null);
    setJourneys([]);
    setError(null);
    latestRequestedId.current = null;

    const userId = SessionStore.getUser()?.id;
    fetchJourneysCreatedByUser(userId, getDateString())
      .then((list) => setJourneys((list || []).filter((j) => CADENCE_1_JOURNEY_TYPES.includes(j.journey_type))))
      .catch(() => setError('Could not load cadences. Please try again.'))
      .finally(() => setLoadingJourneys(false));
  }, [isOpen]);

  const handleJourneySelect = async (journeyId) => {
    setSelectedJourneyId(journeyId);
    setPreviewSteps(null);
    if (!journeyId) {
      latestRequestedId.current = null;
      setLoadingSteps(false);
      return;
    }

    latestRequestedId.current = journeyId;
    setLoadingSteps(true);
    setError(null);
    try {
      const userId = SessionStore.getUser()?.id;
      const today = getDateString();
      const steps = await fetchStepsByJourneyId(Number(journeyId), userId, today, null);
      if (latestRequestedId.current !== journeyId) return;
      setPreviewSteps(convertJourneyStepsToV2Steps(steps));
    } catch {
      if (latestRequestedId.current !== journeyId) return;
      setError('Could not load cadence steps. Please try again.');
    } finally {
      if (latestRequestedId.current === journeyId) setLoadingSteps(false);
    }
  };

  const handleImport = () => {
    if (previewSteps) {
      onImport(previewSteps);
      onClose();
    }
  };

  const selectedJourney = journeys.find((j) => String(j.id) === String(selectedJourneyId));

  return (
    <Modal isOpen={isOpen} closeModal={onClose} size="medium">
      <ModalHeader title="Import from Cadence 1.0" closeModal={onClose} />
      <ModalBody>
        <div className="import-journey-modal">
          <p className="import-journey-modal--desc">
            Select a Cadence 1.0 to import its steps as a starting point. Your task steps and timing will be copied over
            — you can edit them freely before saving.
          </p>

          <div className="import-journey-modal--field">
            <label className="import-journey-modal--label" htmlFor="import-journey-select">
              Select a Cadence 1.0
            </label>
            {loadingJourneys ? (
              <p className="import-journey-modal--loading">Loading cadences...</p>
            ) : (
              <select
                id="import-journey-select"
                className="import-journey-modal--select"
                value={selectedJourneyId}
                onChange={(e) => handleJourneySelect(e.target.value)}
              >
                <option value="">Choose a cadence...</option>
                {journeys.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {error && <p className="import-journey-modal--error">{error}</p>}

          {loadingSteps && <p className="import-journey-modal--loading">Loading steps...</p>}

          {previewSteps && !loadingSteps && (
            <div className="import-journey-modal--preview">
              <h4 className="import-journey-modal--preview-title">
                Steps to import from &ldquo;{selectedJourney?.name}&rdquo;
              </h4>
              {previewSteps.length === 0 ? (
                <p className="import-journey-modal--empty">This cadence has no active steps.</p>
              ) : (
                <ol className="import-journey-modal--step-list">
                  {previewSteps.map((step) => {
                    if (step.type === 'wait') {
                      return (
                        <li key={step.id} className="import-journey-modal--step import-journey-modal--step-wait">
                          <Icon icon="access-time" />
                          <span>
                            Wait {step.waitDuration} {step.waitUnit}
                          </span>
                        </li>
                      );
                    }
                    const taskInfo = getTaskTypeInfo(step.taskType);
                    return (
                      <li key={step.id} className="import-journey-modal--step">
                        <Icon icon={taskInfo?.icon || 'task'} />
                        <div>
                          <span className="import-journey-modal--step-name">{step.name || taskInfo?.label}</span>
                          <span className="import-journey-modal--step-type">{taskInfo?.label}</span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}

          <div className="import-journey-modal--footer">
            <button type="button" className="import-journey-modal--cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="import-journey-modal--confirm"
              disabled={!previewSteps || previewSteps.length === 0 || loadingSteps}
              onClick={handleImport}
            >
              Import Steps
            </button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

ImportJourneyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
};

export default ImportJourneyModal;
